import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { invalidateEntity } from '../cache-invalidation';
import {
  authKeys,
  departmentKeys,
  initializeKeys,
  organizationKeys,
  orgFunctionKeys,
  orgMemberKeys
} from '../query-keys';
import {
  addDepartmentContract,
  updateDepartmentContract,
  deleteDepartmentContract,
  InitializeAddDepartmentsContract,
  InitializeValidateDepartmentsContract,
  type AddDepartmentRequest,
  type AddDepartmentResponse,
  type UpdateDepartmentRequest,
  type UpdateDepartmentResponse,
  type DeleteDepartmentResponse,
  type InitializeAddDepartmentsRequest,
  type InitializeAddDepartmentsResponse,
  type InitializeValidateDepartmentsRequest,
  type InitializeValidateDepartmentsResponse
} from '@bliks-io/contracts';

/**
 * Create department mutation
 *
 * Creates a new department using the single department endpoint.
 * - Invalidates departments list cache
 * - Returns the created department
 */
export function useCreateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddDepartmentRequest): Promise<AddDepartmentResponse> => {
      const { data: response } = await apiClient.post<AddDepartmentResponse>(
        addDepartmentContract.path,
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
    }
  });
}

/**
 * Update department mutation
 *
 * Updates an existing department by ID.
 * - Invalidates both list and detail caches
 * - Returns the updated department
 */
export function useUpdateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: UpdateDepartmentRequest;
    }): Promise<UpdateDepartmentResponse> => {
      const path = updateDepartmentContract.path.replace(':id', id);
      const { data: response } = await apiClient.patch<UpdateDepartmentResponse>(path, data);
      return response;
    },
    onSuccess: () => {
      invalidateEntity(queryClient, 'department');
    }
  });
}

/**
 * Delete department mutation
 *
 * Deletes a department by ID.
 * - Invalidates departments list cache
 * - Removes department detail from cache
 */
export function useDeleteDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<DeleteDepartmentResponse> => {
      const path = deleteDepartmentContract.path.replace(':id', id);
      const { data } = await apiClient.delete<DeleteDepartmentResponse>(path);
      return data;
    },
    onSuccess: (_, id) => {
      invalidateEntity(queryClient, 'department');
      queryClient.removeQueries({ queryKey: departmentKeys.detail(id) });
    }
  });
}

/**
 * Validate departments bulk mutation
 *
 * Validates department data before bulk creation.
 * - Resolves parent abbreviations to ObjectIds
 * - Resolves manager emails to user ObjectIds
 * - Checks for circular dependencies
 * - Returns validation results with creation order
 * - Does NOT invalidate cache (read-only operation)
 */
export function useInitializeValidateDepartments() {
  return useMutation({
    mutationFn: async (
      data: InitializeValidateDepartmentsRequest
    ): Promise<InitializeValidateDepartmentsResponse> => {
      const { data: response } = await apiClient.post<InitializeValidateDepartmentsResponse>(
        InitializeValidateDepartmentsContract.path,
        data
      );
      return response;
    }
  });
}

/**
 * Create departments bulk mutation
 *
 * Creates multiple departments in bulk with hierarchical relationships.
 * - Expects pre-validated data from validation endpoint
 * - Creates departments in topological order
 * - Supports intra-batch parent references
 * - Invalidates departments list cache on success
 */
export function useCreateDepartmentsBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: InitializeAddDepartmentsRequest
    ): Promise<InitializeAddDepartmentsResponse> => {
      const { data: response } = await apiClient.post<InitializeAddDepartmentsResponse>(
        InitializeAddDepartmentsContract.path,
        data
      );
      return response;
    },
    onSuccess: () =>
      // Awaited (returned) on purpose: the wizard derives step reachability
      // from the organization's initialization stamps, so navigation after
      // mutateAsync must see the refetched progress or it clamps back.
      Promise.all([
        queryClient.invalidateQueries({ queryKey: departmentKeys.all }),
        queryClient.invalidateQueries({ queryKey: initializeKeys.unresolvedDepartments() }),
        // A department removal can strand users without a department — the
        // summary's gap counts feed the overview's completion-blocked alert.
        queryClient.invalidateQueries({ queryKey: initializeKeys.summary() }),
        // A surgical removal unsets `department` on users and org functions
        // referencing the deleted department (ADR 0011, third amendment).
        queryClient.invalidateQueries({ queryKey: orgMemberKeys.all }),
        queryClient.invalidateQueries({ queryKey: orgFunctionKeys.all }),
        queryClient.invalidateQueries({ queryKey: organizationKeys.all }),
        // The routing gate reads setup state from the profile cache.
        queryClient.invalidateQueries({ queryKey: authKeys.profile() })
      ])
  });
}
